import React, { CSSProperties } from 'react';

import { formatCurrency } from '~libs/dataUtils';

const MARKER_WARNING_THRESHOLD = 1.1; // 10% above marker

const formatHours = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}h`;
};

const formatValue = (value: number, units: 'currency' | 'hours' = 'currency'): string => {
  return units === 'hours' ? formatHours(value) : formatCurrency(value);
};

export interface ProgressBarData {
  used: number;
  total: number;
  target?: number;
  units?: 'currency' | 'hours';
}

export interface ProgressBarColours {
  normal?: string;
  warning?: string;
  danger?: string;
  background?: string;
  marker?: string;
  text?: {
    used?: string;
    total?: string;
    title?: string;
    label?: string;
  };
}

export interface ProgressBarProps {
  data: ProgressBarData;
  title?: string;
  label?: string;
  height?: number;
  width?: string;
  showTooltip?: boolean;
  showLabel?: boolean;
  colours?: ProgressBarColours;
}

const getFillColour = (
  clampedUsed: number,
  target?: number,
  colours?: ProgressBarColours,
): { className: string; style?: CSSProperties } => {
  if (target === undefined) {
    return {
      className: 'progress-bar-container__fill--normal',
      style: colours?.normal ? { backgroundColor: colours.normal } : undefined,
    };
  }

  const warningThreshold = target * MARKER_WARNING_THRESHOLD;

  if (clampedUsed <= target) {
    return {
      className: 'progress-bar-container__fill--normal',
      style: colours?.normal ? { backgroundColor: colours.normal } : undefined,
    };
  } else if (clampedUsed <= warningThreshold) {
    return {
      className: 'progress-bar-container__fill--warning',
      style: colours?.warning ? { backgroundColor: colours.warning } : undefined,
    };
  } else {
    return {
      className: 'progress-bar-container__fill--danger',
      style: colours?.danger ? { backgroundColor: colours.danger } : undefined,
    };
  }
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  data,
  title,
  label,
  height = 50,
  width = '100%',
  showTooltip = true,
  showLabel = false,
  colours,
}) => {
  const { used = 0, total = 0, target, units = 'currency' } = data;

  // Safety checks for invalid values
  const safeUsed = isNaN(used) ? 0 : Math.max(0, used);
  const safeTotal = isNaN(total) || total <= 0 ? 0 : Math.max(0, total);

  // Clamp usage at total
  const clampedUsed = Math.min(safeUsed, safeTotal);

  // Percentages for fill and marker - handle division by zero
  const percentage = safeTotal > 0 ? (clampedUsed / safeTotal) * 100 : 0;
  const rawMarker = target !== undefined && safeTotal > 0 ? (target / safeTotal) * 100 : null;
  const markerPosition = rawMarker !== null ? Math.min(100, Math.max(0, rawMarker)) : null;

  // Determine fill colour
  const fillColour = getFillColour(clampedUsed, target, colours);

  // Header section
  const renderHeader = () => {
    if (!title && !showLabel) return null;

    return (
      <div className="progress-bar-container__header">
        {title && (
          <span
            className="progress-bar-container__title"
            style={{ ...(colours?.text?.title && { color: colours.text.title }) }}
          >
            {title}
          </span>
        )}

        {showLabel && (
          <span
            className="progress-bar-container__label"
            style={{ ...(colours?.text?.label && { color: colours.text.label }) }}
          >
            {label ||
              `${formatValue(clampedUsed, units)} / ${formatValue(safeTotal, units)} (${isNaN(percentage) ? 0 : Math.round(percentage)}%)`}
          </span>
        )}
      </div>
    );
  };

  // Tooltip text
  const getTooltipText = (): string | undefined => {
    if (!showTooltip || target === undefined) return undefined;

    const targetPercentage = safeTotal > 0 ? Math.round((target / safeTotal) * 100) : 0;
    return `Expected: ${formatValue(target, units)} (${isNaN(targetPercentage) ? 0 : targetPercentage}%)`;
  };

  return (
    <div className="progress-bar-container" style={{ width }}>
      {renderHeader()}

      <div className="progress-bar-container__wrapper">
        <div
          className="progress-bar-container__track"
          style={{
            height: `${height}px`,
            ...(colours?.background && { backgroundColor: colours.background }),
          }}
          title={getTooltipText()}
        >
          {/* Progress fill */}
          <div
            className={`progress-bar-container__fill ${fillColour.className}`}
            style={{ width: `${percentage}%`, ...fillColour.style }}
          />

          {/* Text overlays */}
          {height > 20 && percentage > 0 && (
            <div
              className="progress-bar-container__text progress-bar-container__text--used"
              style={{
                left: `${percentage / 2}%`,
                ...(colours?.text?.used && { color: colours.text.used }),
              }}
            >
              {formatValue(clampedUsed, units)}
            </div>
          )}

          {height > 20 && (
            <div
              className="progress-bar-container__text progress-bar-container__text--total"
              style={{
                ...(colours?.text?.total && { color: colours.text.total }),
              }}
            >
              {formatValue(safeTotal, units)}
            </div>
          )}
        </div>

        {/* Target marker */}
        {markerPosition !== null && (
          <div
            className="progress-bar-container__marker"
            style={{
              left: `${markerPosition}%`,
              height: `${height + 12}px`,
              ...(colours?.marker && { backgroundColor: colours.marker }),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
